import React, { useCallback, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Alert,
  Paper,
  useTheme,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Close as CloseIcon,
  Image as ImageIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { timelineService } from '../../services/timelineService';

interface TimelineImageUploaderProps {
  value?: string;
  onChange: (imageUrl: string | undefined) => void;
  disabled?: boolean;
  error?: string;
}

/**
 * API エラー型定義
 */
interface ApiError {
  response?: {
    status: number;
    data?: {
      message?: string;
      details?: string;
    };
  };
  code?: string;
  message?: string;
}

/**
 * 画像アップロードのバリデーション定数
 */
const IMAGE_VALIDATION = {
  MAX_SIZE: 5 * 1024 * 1024, // 5MB
  MAX_SIZE_MB: 5,
  ACCEPTED_TYPES: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'],
  ACCEPTED_FORMATS: 'JPG、PNG、GIF',
} as const;

/**
 * エラーメッセージ定数（クライアント/サーバー共通）
 */
const ERROR_MESSAGES = {
  INVALID_FILE_TYPE: `サポートされていないファイル形式です。${IMAGE_VALIDATION.ACCEPTED_FORMATS}形式の画像を選択してください。`,
  FILE_TOO_LARGE: `ファイルサイズが大きすぎます。${IMAGE_VALIDATION.MAX_SIZE_MB}MB以下の画像を選択してください。`,
  NETWORK_ERROR: 'ネットワークエラーが発生しました。インターネット接続を確認してください。',
  CONNECTION_ERROR: 'サーバーに接続できませんでした。しばらくしてから再度お試しください。',
  TIMEOUT_ERROR: 'アップロードがタイムアウトしました。ネットワーク接続を確認してください。',
  AUTH_ERROR: '認証エラーです。再度ログインしてください。',
  PERMISSION_ERROR: 'この操作を実行する権限がありません。',
  SERVER_ERROR: 'サーバーエラーが発生しました。しばらくしてから再度お試しください。',
  UNKNOWN_ERROR: '画像のアップロードに失敗しました。',
} as const;

export const TimelineImageUploader: React.FC<TimelineImageUploaderProps> = ({
  value,
  onChange,
  disabled = false,
  error,
}) => {
  const theme = useTheme();
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);

  /**
   * エラーメッセージを取得（サーバーエラーを統一されたメッセージに変換）
   */
  const getErrorMessage = (error: ApiError): string => {
    // ネットワークエラー
    if (!error.response) {
      if (error.code === 'ECONNABORTED') {
        return ERROR_MESSAGES.TIMEOUT_ERROR;
      }
      if (error.message === 'Network Error') {
        return ERROR_MESSAGES.NETWORK_ERROR;
      }
      return ERROR_MESSAGES.CONNECTION_ERROR;
    }

    // HTTPステータスコードによる分類
    const status = error.response.status;
    const serverMessage = error.response?.data?.message;
    const details = error.response?.data?.details;

    switch (status) {
      case 400:
        // バリデーションエラー - サーバーからの詳細メッセージを優先
        if (details) {
          return details;
        }
        // サーバーメッセージの内容に応じて統一メッセージを返す
        if (serverMessage?.includes('size') || serverMessage?.includes('サイズ')) {
          return ERROR_MESSAGES.FILE_TOO_LARGE;
        }
        if (serverMessage?.includes('type') || serverMessage?.includes('形式')) {
          return ERROR_MESSAGES.INVALID_FILE_TYPE;
        }
        return serverMessage || ERROR_MESSAGES.UNKNOWN_ERROR;

      case 401:
        return ERROR_MESSAGES.AUTH_ERROR;

      case 403:
        return ERROR_MESSAGES.PERMISSION_ERROR;

      case 413:
        return ERROR_MESSAGES.FILE_TOO_LARGE;

      case 415:
        return ERROR_MESSAGES.INVALID_FILE_TYPE;

      case 500:
      case 502:
      case 503:
      case 504:
        return ERROR_MESSAGES.SERVER_ERROR;

      default:
        return serverMessage || ERROR_MESSAGES.UNKNOWN_ERROR;
    }
  };

  const handleFileChange = useCallback(
    async (file: File) => {
      if (!file) return;

      // クライアント側バリデーション（UX向上のため、サーバー側と同じルール）
      if (!file.type.startsWith('image/')) {
        setUploadError(ERROR_MESSAGES.INVALID_FILE_TYPE);
        return;
      }

      if (file.size > IMAGE_VALIDATION.MAX_SIZE) {
        setUploadError(ERROR_MESSAGES.FILE_TOO_LARGE);
        return;
      }

      setUploading(true);
      setUploadError(null);

      try {
        const response = await timelineService.uploadImage(file);
        onChange(response.data.image_url);
      } catch (error) {
        console.error('画像アップロードエラー:', error);
        const errorMessage = getErrorMessage(error as ApiError);
        setUploadError(errorMessage);
      } finally {
        setUploading(false);
      }
    },
    [onChange]
  );

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileChange(file);
    }
    // 同じファイルを再選択できるようにリセット
    event.target.value = '';
  };

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setDragActive(false);

      const file = event.dataTransfer.files[0];
      if (file) {
        handleFileChange(file);
      }
    },
    [handleFileChange]
  );

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
  };

  const handleRemove = () => {
    onChange(null);
    setUploadError(null);
  };

  return (
    <Box
      onClick={(e) => e.stopPropagation()}
      onDragOver={(e) => e.stopPropagation()}
      onDrop={(e) => e.stopPropagation()}
    >
      {value ? (
        // 画像プレビュー
        <Paper
          elevation={1}
          sx={{
            position: 'relative',
            overflow: 'hidden',
            borderRadius: 2,
            maxWidth: 400,
          }}
        >
          <img
            src={value}
            alt="タイムライン画像"
            style={{
              width: '100%',
              height: 'auto',
              maxHeight: 300,
              objectFit: 'cover',
              display: 'block',
            }}
          />
          {!disabled && (
            <>
              {/* 変更ボタン */}
              <Tooltip title="画像を変更">
                <IconButton
                  component="label"
                  sx={{
                    position: 'absolute',
                    top: 8,
                    left: 8,
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    },
                  }}
                  size="small"
                  disabled={uploading}
                >
                  <EditIcon fontSize="small" />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleInputChange}
                    disabled={uploading}
                    hidden
                  />
                </IconButton>
              </Tooltip>
              {/* 削除ボタン */}
              <Tooltip title="画像を削除">
                <IconButton
                  onClick={handleRemove}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    },
                  }}
                  size="small"
                  disabled={uploading}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
          {/* アップロード中のオーバーレイ */}
          {uploading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                color: 'white',
              }}
            >
              <CircularProgress size={40} sx={{ color: 'white' }} />
              <Typography variant="body2" sx={{ mt: 1 }}>
                アップロード中...
              </Typography>
            </Box>
          )}
        </Paper>
      ) : (
        // ファイルドロップエリア
        <Paper
          elevation={1}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          sx={{
            border: `2px dashed ${
              dragActive ? theme.palette.primary.main : theme.palette.divider
            }`,
            borderRadius: 2,
            padding: 3,
            textAlign: 'center',
            position: 'relative',
            backgroundColor: dragActive
              ? theme.palette.action.hover
              : 'transparent',
            transition: 'all 0.2s ease',
            '&:hover': !disabled &&
              !uploading && {
                backgroundColor: theme.palette.action.hover,
                borderColor: theme.palette.primary.main,
              },
          }}
        >
          <input
            type="file"
            accept="image/*"
            onChange={handleInputChange}
            disabled={disabled || uploading}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              opacity: 0,
              cursor: disabled || uploading ? 'not-allowed' : 'pointer',
              zIndex: 1,
            }}
          />

          {uploading ? (
            <Box sx={{ pointerEvents: 'none' }}>
              <CircularProgress size={40} />
              <Typography variant="body2" sx={{ mt: 1 }}>
                アップロード中...
              </Typography>
            </Box>
          ) : (
            <Box sx={{ pointerEvents: 'none' }}>
              <CloudUploadIcon
                sx={{
                  fontSize: 48,
                  color: theme.palette.text.secondary,
                  mb: 1,
                }}
              />
              <Typography variant="h6" color="textSecondary" gutterBottom>
                画像をアップロード
              </Typography>
              <Typography variant="body2" color="textSecondary">
                クリックまたはドラッグ&ドロップ
              </Typography>
              <Typography
                variant="caption"
                color="textSecondary"
                display="block"
                sx={{ mt: 1 }}
              >
                {IMAGE_VALIDATION.ACCEPTED_FORMATS}形式 / 最大{IMAGE_VALIDATION.MAX_SIZE_MB}MB / 1枚のみ
              </Typography>
            </Box>
          )}
        </Paper>
      )}

      {/* エラー表示 */}
      {(uploadError || error) && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {uploadError || error}
        </Alert>
      )}

      {/* 説明文 */}
      <Typography
        variant="caption"
        color="textSecondary"
        sx={{ mt: 1, display: 'block' }}
      >
        <ImageIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
        タイムライン投稿には1枚まで画像を追加できます
      </Typography>
    </Box>
  );
};
